import { Badge } from "../../primitives/badge";
import {
  HEALTH_TIER_LABEL,
  HEALTH_TIER_VARIANT,
  healthScoreTier,
  type HealthTierKey,
} from "./strategyTiers";

interface CustomerHealthBadgeProps {
  /** Composite 0–100 account-health score; null when no signals exist. */
  score: number | null;
  /** Append the numeric score to the tier label (e.g. "Watch · 54%"). */
  showScore?: boolean;
  /**
   * A rep-set manual override tier, when one is active. Present only
   * together with ``isOverridden``. When set, the badge renders this tier
   * instead of deriving one from ``score``. Typed loosely (matches the
   * backend's plain ``str | None`` field) rather than as ``HealthTierKey``
   * so a generated-type drift degrades to "no badge", never a build error.
   */
  overrideTier?: string | null;
  /** True when ``overrideTier`` reflects an active manual override. */
  isOverridden?: boolean;
}

function isHealthTierKey(value: string): value is HealthTierKey {
  return value in HEALTH_TIER_LABEL;
}

/**
 * At-a-glance account-health indicator — a coloured tier pill (Thriving /
 * Healthy / Watch / At risk) so a rep sees a customer's health before
 * scrolling or opening the customer. Colour and label come from the shared
 * ``healthScoreTier`` / ``HEALTH_TIER_VARIANT`` mappings, so this always
 * agrees with the detailed Account health card. Renders nothing when there
 * is neither a score nor an active override.
 */
export function CustomerHealthBadge({
  score,
  showScore = false,
  overrideTier,
  isOverridden = false,
}: CustomerHealthBadgeProps) {
  const validOverrideTier =
    isOverridden && overrideTier && isHealthTierKey(overrideTier)
      ? overrideTier
      : undefined;
  const tier = validOverrideTier
    ? {
        label: HEALTH_TIER_LABEL[validOverrideTier],
        variant: HEALTH_TIER_VARIANT[validOverrideTier],
      }
    : healthScoreTier(score);
  if (!tier || (score == null && !validOverrideTier)) return null;
  const isOverriddenDisplay = Boolean(validOverrideTier);

  const label =
    showScore && score != null && !isOverriddenDisplay
      ? `${tier.label} · ${score}%`
      : tier.label;

  return (
    <Badge
      variant={tier.variant}
      title={
        isOverriddenDisplay
          ? `Account health: ${tier.label} (manually overridden)`
          : `Account health: ${tier.label}${score != null ? ` (${score}%)` : ""}`
      }
    >
      {label}
      {isOverriddenDisplay ? <span aria-hidden="true"> ✎</span> : null}
    </Badge>
  );
}
